<template>
  <div class="list-owe-fee-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('listOweFee.table.title')" :subtitle="$t('listOweFee.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-download" @click="exportFee">
          {{ $t('common.export') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="listOweFeeInfo.conditions.floorName" clearable class="w-full"
            :placeholder="$t('listOweFee.search.floorName')">
            <el-button slot="append" icon="el-icon-search" @click="openChooseFloor">
              {{ $t('common.select') }}
            </el-button>
          </el-input>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="listOweFeeInfo.conditions.unitId" clearable class="w-full"
            :placeholder="$t('listOweFee.search.unit')">
            <el-option :label="$t('listOweFee.search.unit')" value="" />
            <el-option v-for="(unit, index) in listOweFeeInfo.roomUnits" :key="index"
              :label="unit.unitNum + $t('listOweFee.search.unit')" :value="unit.unitId"></el-option>
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="listOweFeeInfo.conditions.roomNum" clearable class="w-full"
            :placeholder="$t('listOweFee.search.roomNum')" @keyup.enter.native="queryOweFee" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="listOweFeeInfo.conditions.roomSubType" clearable class="w-full"
            :placeholder="$t('listOweFee.search.roomType')">
            <el-option :label="$t('listOweFee.search.roomType')" value="" />
            <el-option v-for="(item, index) in listOweFeeInfo.roomSubTypes" :key="index" :label="item.name"
              :value="item.statusCd"></el-option>
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="listOweFeeInfo.conditions.ownerName" clearable class="w-full"
            :placeholder="$t('listOweFee.search.ownerName')" @keyup.enter.native="queryOweFee" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="listOweFeeInfo.conditions.payObjType" clearable class="w-full"
            :placeholder="$t('listOweFee.search.payObjType')">
            <el-option :label="$t('listOweFee.search.payObjType')" value="" />
            <el-option v-for="(item, index) in listOweFeeInfo.payObjTypes" :key="index" :label="item.name"
              :value="item.statusCd"></el-option>
          </el-select>
        </el-col>
        <el-col v-if="listOweFeeInfo.conditions.payObjType === '3333'" :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="listOweFeeInfo.conditions.num" clearable class="w-full"
            :placeholder="$t('listOweFee.search.roomNumFormat')" @keyup.enter.native="queryOweFee" />
        </el-col>
        <el-col v-if="listOweFeeInfo.conditions.payObjType === '6666'" :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="listOweFeeInfo.conditions.num" clearable class="w-full"
            :placeholder="$t('listOweFee.search.carportNum')" @keyup.enter.native="queryOweFee" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="selectedFeeConfigs" multiple collapse-tags class="w-full"
            :placeholder="$t('listOweFee.search.feeConfig')">
            <el-option v-for="(item, index) in listOweFeeInfo.feeConfigs" :key="index" :label="item.feeName"
              :value="item.configId"></el-option>
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="queryOweFee">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetOweFee">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="loadListOweFeeInfo">
        <template #left>
          <span class="ds-panel__title">{{ $t('listOweFee.table.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="listOweFeeInfo.fees" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('listOweFee.page.empty')" @retry="loadListOweFeeInfo">
        <el-table-column prop="index" :label="$t('listOweFee.table.index')" width="70" align="center">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.$index + 1 }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="payerObjName" :label="$t('listOweFee.table.payerObjName')" align="left" min-width="150"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.payerObjName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="ownerName" :label="$t('listOweFee.table.ownerName')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.ownerName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="ownerTel" :label="$t('listOweFee.table.ownerTel')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.ownerTel || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="endTime" :label="$t('listOweFee.table.endTime')" align="center" width="170">
          <template slot-scope="scope">
            {{ formatDateTimeSec(scope.row.endTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="deadlineTime" :label="$t('listOweFee.table.deadlineTime')" align="center" width="170">
          <template slot-scope="scope">
            {{ formatDateTimeSec(scope.row.deadlineTime) }}
          </template>
        </el-table-column>
        <el-table-column v-for="(item, index) in listOweFeeInfo.feeConfigNames" :key="index" :label="item.configName"
          align="right" min-width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(getFeeOweAmount(item, scope.row)) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('listOweFee.table.total')" align="right" width="130">
          <template slot-scope="scope">
            <span class="ds-num ds-num--strong">{{ formatMoney(getAllFeeOweAmount(scope.row)) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="updateTime" :label="$t('listOweFee.table.updateTime')" align="center" width="170">
          <template slot-scope="scope">
            {{ formatDateTimeSec(scope.row.updateTime) }}
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <div class="summary-wrapper">
            <div class="summary-item">
              <span class="summary-title">{{ $t('listOweFee.summary.subtotal') }}</span>
              <div v-for="(item, index) in listOweFeeInfo.feeConfigNames" :key="index" class="summary-content">
                {{ item.configName }}: {{ formatMoney(getFeeOweAllAmount(item)) }} {{ $t('common.yuan') }}
              </div>
              <div class="summary-content">
                {{ $t('listOweFee.summary.total') }}: {{ formatMoney(getFeeOweAllAmounts()) }} {{ $t('common.yuan') }}
              </div>
            </div>
          </div>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 选择楼栋组件 -->
    <search-floor ref="searchFloor" @chooseFloor="handleChooseFloor"></search-floor>
  </div>
</template>

<script>
import { getDict } from '@/api/community/communityApi'
import { getCommunityId } from '@/api/community/communityApi'
import { queryReportOweFee, listFeeConfigs, exportData, queryUnits } from '@/api/fee/listOweFeeApi'
import SearchFloor from '@/components/fee/searchFloor'
import { formatDateTimeSec, formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ListOweFee',
  components: {
    SearchFloor,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      listOweFeeInfo: {
        fees: [],
        moreCondition: false,
        roomUnits: [],
        conditions: {
          configIds: '',
          payObjType: '',
          num: '',
          billType: '00123',
          floorId: '',
          floorName: '',
          roomNum: '',
          unitId: '',
          roomSubType: '',
          ownerName: '',
          communityId: ''
        },
        feeConfigs: [],
        feeConfigNames: [],
        payObjTypes: [],
        roomSubTypes: []
      },
      selectedFeeConfigs: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.listOweFeeInfo.conditions.communityId = getCommunityId()
    this.initData()
  },
  watch: {
    selectedFeeConfigs(newVal) {
      this.listOweFeeInfo.feeConfigNames = newVal.map(configId => {
        const config = this.listOweFeeInfo.feeConfigs.find(item => item.configId === configId)
        return {
          configId,
          configName: config ? config.feeName : ''
        }
      })
    }
  },
  methods: {
    async initData() {
      try {
        this.loading = true
        await Promise.all([
          this.getPayObjTypes(),
          this.getRoomSubTypes(),
          this.loadListOweFeeInfo(),
          this.listFeeConfigs()
        ])
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('listOweFee.page.loadFailed')
        console.error('初始化数据失败:', error)
      } finally {
        this.loading = false
      }
    },
    async getPayObjTypes() {
      try {
        const data = await getDict('report_owe_fee', 'payer_obj_type')
        this.listOweFeeInfo.payObjTypes = data
      } catch (error) {
        console.error('获取付费对象类型失败:', error)
      }
    },
    async getRoomSubTypes() {
      try {
        const data = await getDict('building_room', 'room_sub_type')
        this.listOweFeeInfo.roomSubTypes = data
      } catch (error) {
        console.error('获取房屋类型失败:', error)
      }
    },
    async loadListOweFeeInfo() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.listOweFeeInfo.conditions,
          page: this.page.current,
          row: this.page.size,
          configIds: this.listOweFeeInfo.feeConfigNames.map(item => item.configId).join(',')
        }
        const { data, total } = await queryReportOweFee(params)
        this.listOweFeeInfo.fees = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('listOweFee.page.loadFailed')
        console.error('获取欠费信息失败:', error)
      } finally {
        this.loading = false
      }
    },
    async listFeeConfigs() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: this.listOweFeeInfo.conditions.communityId
        }
        const { data } = await listFeeConfigs(params)
        this.listOweFeeInfo.feeConfigs = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('获取收费项失败:', error)
      }
    },
    toggleMoreCondition() {
      this.listOweFeeInfo.moreCondition = !this.listOweFeeInfo.moreCondition
    },
    queryOweFee() {
      this.page.current = 1
      this.loadListOweFeeInfo()
    },
    resetOweFee() {
      this.listOweFeeInfo.conditions = {
        configIds: '',
        payObjType: '',
        num: '',
        billType: '00123',
        floorId: '',
        floorName: '',
        roomNum: '',
        unitId: '',
        roomSubType: '',
        ownerName: '',
        communityId: this.listOweFeeInfo.conditions.communityId
      }
      this.selectedFeeConfigs = []
      this.loadListOweFeeInfo()
    },
    getFeeOweAmount(item, fee) {
      if (!fee.items) return 0
      const found = fee.items.find(tmp => tmp.configId === item.configId)
      return found ? found.amountOwed : 0
    },
    getFeeOweAllAmount(item) {
      let amountOwed = 0.0
      this.listOweFeeInfo.fees.forEach(feeItem => {
        if (!feeItem.items) return
        feeItem.items.forEach(tmp => {
          if (tmp.configId === item.configId) {
            amountOwed += parseFloat(tmp.amountOwed)
          }
        })
      })
      return amountOwed.toFixed(2)
    },
    getAllFeeOweAmount(fee) {
      if (this.listOweFeeInfo.feeConfigNames.length < 1) {
        return fee.amountOwed
      }
      let amountOwed = 0.0
      this.listOweFeeInfo.feeConfigNames.forEach(feeItem => {
        if (!fee.items) return
        fee.items.forEach(item => {
          if (feeItem.configId === item.configId) {
            amountOwed += parseFloat(item.amountOwed)
          }
        })
      })
      return amountOwed.toFixed(2)
    },
    getFeeOweAllAmounts() {
      let amountOwed = 0.0
      this.listOweFeeInfo.fees.forEach(feeItem => {
        const rowAmount = this.getAllFeeOweAmount(feeItem)
        amountOwed += parseFloat(rowAmount)
      })
      return amountOwed.toFixed(2)
    },
    async exportFee() {
      try {
        const params = {
          ...this.listOweFeeInfo.conditions,
          pagePath: 'reportListOweFee',
          configIds: this.listOweFeeInfo.feeConfigNames.map(item => item.configId).join(',')
        }
        await exportData(params)
        this.$message.success(this.$t('common.operationSuccess'))
        this.$router.push('/pages/property/downloadTempFile?tab=下载中心')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error(this.$t('listOweFee.exportError'))
      }
    },
    handleSizeChange(val) {
      this.page.size = val
      this.loadListOweFeeInfo()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.loadListOweFeeInfo()
    },
    openChooseFloor() {
      this.$refs.searchFloor.open()
    },
    handleChooseFloor(floor) {
      this.listOweFeeInfo.conditions.floorId = floor.floorId
      this.listOweFeeInfo.conditions.floorName = floor.floorName
      this.loadUnits(floor.floorId)
    },
    async loadUnits(floorId) {
      try {
        const params = {
          floorId,
          communityId: this.listOweFeeInfo.conditions.communityId
        }
        const { data } = await queryUnits(params)
        this.listOweFeeInfo.roomUnits = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('获取单元信息失败:', error)
      }
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.list-owe-fee-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧小计/合计 */
.summary-wrapper {
  padding: 0;
  background-color: transparent;
}

.summary-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.summary-title {
  font-weight: 600;
  margin-right: var(--ds-spacing-lg);
  color: var(--ds-gray-800);
}

.summary-content {
  margin-right: var(--ds-spacing-lg);
  margin-bottom: var(--ds-spacing-sm);
  font-size: var(--ds-font-size-sm);
  color: var(--ds-gray-600);
}
</style>
